<template>
  <div>
    <div
      v-editable="blok"
      class="cms-text break-words"
      :class="[
        {
          'text-center': alignment === 'centered',
          'text-right': alignment === 'right',
          'text-justify': alignment === 'justify',
        },
        marginClasses,
      ]"
    >
      <div
        v-if="isCollapsible"
        ref="textContainer"
        :style="{ maxHeight }"
        class="transition-height overflow-hidden duration-500 ease-in-out"
        :class="{ 'collapsed-text': !isExpanded }"
        tabindex="-1"
        role="region"
        :aria-label="$t('global.collapsible_content')"
      >
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="content" />
      </div>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-else v-html="content" />
    </div>
    <Button
      v-if="isCollapsible"
      class="mt-4 px-4 py-2"
      type="underlined"
      ignore-capitalisation
      no-padding
      :aria-expanded="isExpanded.toString()"
      @click="toggleExpand"
    >
      <span>{{
        $t(!isExpanded ? 'global.show_more' : 'global.show_less')
      }}</span>
      <IconArrowDown
        class="size-4 transition"
        :class="{ '-rotate-180': isExpanded }"
      />
    </Button>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, nextTick } from 'vue'
import type { SbFoldableText } from '~/cms/storyblok/types/storyblok.d.ts'
import useStoryblokMargins from '~/cms/storyblok/composables/useStoryblokMargins'
import useRichtext from '~/cms/storyblok/composables/useRichtext'
import { useDeviceType } from '~/composables/useDeviceType'

const props = defineProps({
  blok: {
    type: Object as PropType<SbFoldableText>,
    default: () => ({}),
  },
})

const { isMobileOrTablet } = useDeviceType()
const { marginClasses } = useStoryblokMargins(props.blok)
const { content } = useRichtext(props.blok.body as any)

const textContainer = ref<HTMLElement | null>(null)
const isFoldedMobile = ref(props.blok.folded_mobile)
const isFoldedDesktop = ref(props.blok.folded_desktop)
const maxHeight = ref('250px')

const isCollapsible = computed(() =>
  isMobileOrTablet.value ? isFoldedMobile.value : isFoldedDesktop.value,
)
const isExpanded = ref(false)

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value

  nextTick(() => {
    updateMaxHeight()
    updateTabIndex()
    if (isExpanded.value) {
      focusContentContainer()
    }
  })
}

const updateMaxHeight = () => {
  if (textContainer.value) {
    if (isExpanded.value) {
      maxHeight.value = `${textContainer.value.scrollHeight}px`
    } else {
      maxHeight.value = '250px'
    }
  }
}

const updateTabIndex = () => {
  if (textContainer.value) {
    const focusableElements = textContainer.value.querySelectorAll(
      'a, button, input, textarea, select, [tabindex]',
    )
    focusableElements.forEach((el) => {
      if (!isExpanded.value) {
        el.setAttribute('tabindex', '-1')
      } else {
        el.removeAttribute('tabindex')
      }
    })
  }
}
const focusContentContainer = () => {
  if (textContainer.value) {
    textContainer.value.focus()
  }
}

watch(isExpanded, () => {
  nextTick(() => {
    updateMaxHeight()
    updateTabIndex()
  })
})

onMounted(() => {
  updateTabIndex()
})

const alignment = computed(() => props.blok?.alignment || '')
</script>

<style lang="postcss" scoped>
h1 {
  @apply text-2xl
  font-medium
  leading-9
  mb-4
  md:mb-6
  md:text-3xl
  md:leading-10;
}
h2 {
  @apply text-xl
  font-medium
  leading-8
  mb-4
  md:mb-6
  md:text-2xl
  md:leading-9;
}
h3 {
  @apply text-lg
  font-normal
  leading-7
  mb-4
  md:mb-6
  md:text-xl
  md:leading-8;
}
a {
  @apply text-black;
}
button {
  @apply text-black font-medium;
}
p {
  @apply text-base
  font-medium
  leading-5
  mb-4
  md:mb-6
  md:text-lg
  md:leading-7;
}
b {
  @apply font-bold;
}
ul {
  @apply list-disc
  pl-6
  mb-4
  md:mb-6;
}
ul li p {
  @apply mb-2;
}
ol {
  @apply list-decimal
  pl-6
  mb-4
  md:mb-6;
}
ol li p {
  @apply mb-2;
}

/* Default collapsed text - Only show 4 lines */
.collapsed-text {
  display: -webkit-box;
  line-clamp: 4;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  position: relative;
  transition: max-height 0.5s ease-in-out;
  min-height: calc(4 * 1.5em);
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Last 2 lines at 20% opacity */
.collapsed-text::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 50%;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.9) 100%
  );
  pointer-events: none;
}

.transition-height {
  transition: max-height 0.5s ease-in-out;
}
</style>
